import { useCallback } from 'react';
import type { WorkflowBlock } from '@/types/workflow';

export interface CanvasKeyboardNavDeps {
  visibleBlocks: WorkflowBlock[];
  selectedBlockId: string | null;
  onSelectBlock: (id: string) => void;
}

/**
 * Returns a keydown handler that navigates between blocks using arrow keys.
 * Picks the closest block in the direction pressed (spatial roving tabindex).
 */
export function useCanvasKeyboardNav({
  visibleBlocks,
  selectedBlockId,
  onSelectBlock,
}: CanvasKeyboardNavDeps) {
  return useCallback(
    (e: React.KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') return;
      if (!['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) return;

      const currentBlock = visibleBlocks.find(b => b.id === selectedBlockId);
      if (!currentBlock) {
        if (visibleBlocks.length > 0) {
          onSelectBlock(visibleBlocks[0].id);
          requestAnimationFrame(() => {
            const el = document.querySelector(`[data-block-id="${visibleBlocks[0].id}"]`) as HTMLElement | null;
            el?.focus();
          });
        }
        return;
      }

      e.preventDefault();
      const candidates = visibleBlocks.filter(b => b.id !== currentBlock.id);
      if (candidates.length === 0) return;

      let bestBlock: WorkflowBlock | null = null;
      let bestScore = Infinity;

      for (const b of candidates) {
        const dx = b.x - currentBlock.x;
        const dy = b.y - currentBlock.y;
        let valid = false;
        let primary = 0;
        let perp = 0;

        switch (e.key) {
          case 'ArrowRight': valid = dx > 20; primary = dx; perp = Math.abs(dy); break;
          case 'ArrowLeft':  valid = dx < -20; primary = -dx; perp = Math.abs(dy); break;
          case 'ArrowDown':  valid = dy > 20; primary = dy; perp = Math.abs(dx); break;
          case 'ArrowUp':    valid = dy < -20; primary = -dy; perp = Math.abs(dx); break;
        }

        if (!valid) continue;
        const score = primary + perp * 0.5;
        if (score < bestScore) {
          bestScore = score;
          bestBlock = b;
        }
      }

      if (bestBlock) {
        onSelectBlock(bestBlock.id);
        const el = document.querySelector(`[data-block-id="${bestBlock.id}"]`) as HTMLElement | null;
        el?.focus();
      }
    },
    [selectedBlockId, visibleBlocks, onSelectBlock],
  );
}
